import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';
import { supabase } from '../lib/supabase';
import { Loader2, Trash2, Eye, SquarePen as PenSquare, AlertCircle, Columns2 as Columns, Filter, Download, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { useAtom } from 'jotai';
import { formProgressAtom } from '../store/form';
import { generateContract } from '../utils/generateContract';
import { generatePowerOfAttorney } from '../utils/generatePowerOfAttorney';
import { useStockAllocations } from '../hooks/useStockAllocations';
import { StockAllocationsService } from '../services/stockAllocationsService';
import ContractViewModal from './ContractViewModal';

interface HistoryViewProps {
  type: 'contract' | 'power-of-attorney';
  onSelect: () => void;
}

type FilterStatus = 'all' | 'complete' | 'incomplete';
type FilterPlate = 'all' | 'with-plate' | 'without-plate';

const CONTRACT_COLUMNS = {
  status: { label: 'Status', default: true },
  name: { label: 'Nome', default: true },
  cpf: { label: 'CPF', default: true },
  phone: { label: 'Telefone', default: false },
  email: { label: 'Email', default: false },
  location: { label: 'Local Instalacao', default: true },
  technical: { label: 'Config. Tecnica', default: true },
  financial: { label: 'Termos Financeiros', default: true },
  plate: { label: 'Placa', default: true },
  created_at: { label: 'Data Criacao', default: true },
  actions: { label: 'Acoes', default: true }
};

const POA_COLUMNS = {
  name: { label: 'Nome', default: true },
  cpf: { label: 'CPF', default: true },
  rg: { label: 'RG', default: false },
  address: { label: 'Endereco', default: false },
  created_at: { label: 'Data Criacao', default: true },
  actions: { label: 'Acoes', default: true }
};

export default function HistoryView({ type, onSelect }: HistoryViewProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [, setFormProgress] = useAtom(formProgressAtom);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [filterPlate, setFilterPlate] = useState<FilterPlate>('all');
  const [viewingRecord, setViewingRecord] = useState<any>(null);

  const defaultColumns = type === 'contract' ? CONTRACT_COLUMNS : POA_COLUMNS;
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    Object.fromEntries(Object.entries(defaultColumns).map(([key, val]) => [key, val.default]))
  );

  const { data: records, isLoading, refetch } = useQuery(
    ['history', type],
    async () => {
      const query = type === 'contract'
        ? supabase
            .from('customers')
            .select(`
              *,
              installation_locations(*),
              technical_configs(*),
              financial_terms(*)
            `)
            .order('created_at', { ascending: false })
        : supabase
            .from('power_of_attorney')
            .select(`
              id,
              full_name,
              cpf,
              rg,
              issuing_authority,
              street,
              number,
              neighborhood,
              city,
              state,
              created_at,
              user_id
            `)
            .order('created_at', { ascending: false });

      const { data, error } = await query;
      if (error) throw error;
      return data;
    }
  );

  const { allocationsMap, error: stockError, isLoading: stockLoading, refetch: refetchAllocations } = useStockAllocations(type === 'contract');

  const handleRefreshAllocations = useCallback(() => {
    StockAllocationsService.clearCache();
    refetchAllocations();
  }, [refetchAllocations]);

  const handlePreview = async (record: any) => {
    try {
      setIsGenerating(true);

      let blob;
      if (type === 'contract') {
        const [locationRes, technicalRes, financialRes] = await Promise.all([
          supabase.from('installation_locations').select('*').eq('customer_id', record.id).order('created_at', { ascending: false }).limit(1).maybeSingle(),
          supabase.from('technical_configs').select('*').eq('customer_id', record.id).order('created_at', { ascending: false }).limit(1).maybeSingle(),
          supabase.from('financial_terms').select('*').eq('customer_id', record.id).order('created_at', { ascending: false }).limit(1).maybeSingle()
        ]);

        let installments = [];
        if (financialRes.data?.id) {
          const { data: installmentsData } = await supabase
            .from('installments')
            .select('*')
            .eq('financial_terms_id', financialRes.data.id);

          if (installmentsData) {
            installments = installmentsData.map(inst => ({
              method: inst.method,
              amount: inst.amount,
              dueDate: new Date(inst.due_date)
            }));
          }
        }

        const data = {
          customerInfo: {
            fullName: record.full_name,
            cpf: record.cpf,
            rg: record.rg,
            issuingAuthority: record.issuing_authority,
            profession: record.profession,
            nationality: record.nationality,
            phone: record.phone,
            email: record.email,
            maritalStatus: record.marital_status
          },
          installationLocation: locationRes.data ? {
            street: locationRes.data.street,
            number: locationRes.data.number,
            neighborhood: locationRes.data.neighborhood,
            city: locationRes.data.city,
            state: locationRes.data.state,
            zipCode: locationRes.data.zip_code,
            utilityCode: locationRes.data.utility_code,
            utilityCompany: locationRes.data.utility_company,
            installationType: locationRes.data.installation_type
          } : undefined,
          technicalConfig: technicalRes.data ? {
            inverter1: {
              brand: technicalRes.data.inverter1_brand,
              power: technicalRes.data.inverter1_power,
              quantity: technicalRes.data.inverter1_quantity,
              warrantyPeriod: technicalRes.data.inverter1_warranty_period
            },
            inverter2: technicalRes.data.inverter2_brand ? {
              brand: technicalRes.data.inverter2_brand,
              power: technicalRes.data.inverter2_power,
              quantity: technicalRes.data.inverter2_quantity,
              warrantyPeriod: technicalRes.data.inverter2_warranty_period
            } : undefined,
            solarModules: {
              brand: technicalRes.data.solar_modules_brand,
              power: technicalRes.data.solar_modules_power,
              quantity: technicalRes.data.solar_modules_quantity
            },
            installationType: technicalRes.data.installation_type,
            installationDays: technicalRes.data.installation_days
          } : undefined,
          financialTerms: financialRes.data ? {
            totalAmount: financialRes.data.total_amount,
            installments
          } : undefined
        };

        blob = await generateContract(data);
      } else {
        blob = await generatePowerOfAttorney({
          customerInfo: {
            fullName: record.full_name,
            cpf: record.cpf,
            rg: record.rg,
            issuingAuthority: record.issuing_authority,
            nationality: 'Brasileiro(a)',
            profession: '',
            phone: '',
            email: '',
            maritalStatus: 'Single'
          },
          installationLocation: {
            street: record.street,
            number: record.number,
            neighborhood: record.neighborhood,
            city: record.city,
            state: record.state,
            zipCode: '',
            utilityCode: '',
            utilityCompany: '',
            installationType: 'Residential'
          }
        });
      }

      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error previewing document:', error);
      alert('Erro ao visualizar documento. Por favor, tente novamente.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este registro?')) {
      return;
    }

    setIsDeleting(true);
    try {
      const { error } = type === 'contract'
        ? await supabase.from('customers').delete().eq('id', id)
        : await supabase.from('power_of_attorney').delete().eq('id', id);

      if (error) throw error;
      refetch();
    } catch (error) {
      console.error('Error deleting record:', error);
      alert('Erro ao excluir registro');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSelectRecord = async (record: any) => {
    try {
      if (type === 'contract') {
        const [locationRes, technicalRes, financialRes] = await Promise.all([
          supabase.from('installation_locations').select('*').eq('customer_id', record.id).order('created_at', { ascending: false }).limit(1).maybeSingle(),
          supabase.from('technical_configs').select('*').eq('customer_id', record.id).order('created_at', { ascending: false }).limit(1).maybeSingle(),
          supabase.from('financial_terms').select('*').eq('customer_id', record.id).order('created_at', { ascending: false }).limit(1).maybeSingle()
        ]);

        let installments = [];
        if (financialRes.data) {
          const { data: installmentsData } = await supabase
            .from('installments')
            .select('*')
            .eq('financial_terms_id', financialRes.data.id);

          if (installmentsData?.length) {
            installments = installmentsData.map(inst => ({
              method: inst.method,
              amount: inst.amount,
              dueDate: inst.due_date ? new Date(inst.due_date) : new Date()
            }));
          }
        }

        let startingStep = 'customer-info';
        let completedSteps = ['customer-info'];

        if (locationRes.data) {
          completedSteps.push('installation-location');
          if (technicalRes.data) {
            completedSteps.push('technical-config');
            if (financialRes.data) {
              completedSteps.push('financial-terms');
              startingStep = 'review';
            } else {
              startingStep = 'financial-terms';
            }
          } else {
            startingStep = 'technical-config';
          }
        } else {
          startingStep = 'installation-location';
        }

        setFormProgress({
          currentStep: startingStep,
          completedSteps: completedSteps,
          data: {
            customerId: record.id,
            installationLocationId: locationRes.data?.id,
            technicalConfigId: technicalRes.data?.id,
            financialTermsId: financialRes.data?.id,
            customerInfo: {
              fullName: record.full_name,
              cpf: record.cpf,
              rg: record.rg,
              issuingAuthority: record.issuing_authority,
              profession: record.profession,
              nationality: record.nationality,
              phone: record.phone,
              email: record.email,
              maritalStatus: record.marital_status
            },
            installationLocation: locationRes.data ? {
              street: locationRes.data.street,
              number: locationRes.data.number,
              neighborhood: locationRes.data.neighborhood,
              city: locationRes.data.city,
              state: locationRes.data.state,
              zipCode: locationRes.data.zip_code,
              utilityCode: locationRes.data.utility_code,
              utilityCompany: locationRes.data.utility_company,
              installationType: locationRes.data.installation_type
            } : undefined,
            technicalConfig: technicalRes.data ? {
              inverter1: {
                brand: technicalRes.data.inverter1_brand,
                power: technicalRes.data.inverter1_power,
                quantity: technicalRes.data.inverter1_quantity,
                warrantyPeriod: technicalRes.data.inverter1_warranty_period
              },
              inverter2: technicalRes.data.inverter2_brand ? {
                brand: technicalRes.data.inverter2_brand,
                power: technicalRes.data.inverter2_power,
                quantity: technicalRes.data.inverter2_quantity,
                warrantyPeriod: technicalRes.data.inverter2_warranty_period
              } : undefined,
              solarModules: {
                brand: technicalRes.data.solar_modules_brand,
                power: technicalRes.data.solar_modules_power,
                quantity: technicalRes.data.solar_modules_quantity
              },
              installationType: technicalRes.data.installation_type,
              installationDays: technicalRes.data.installation_days
            } : undefined,
            financialTerms: financialRes.data ? {
              totalAmount: financialRes.data.total_amount,
              installments
            } : undefined,
          }
        });
      } else {
        setFormProgress({
          currentStep: null,
          completedSteps: [],
          data: {
            powerOfAttorney: {
              fullName: record.full_name,
              cpf: record.cpf,
              rg: record.rg,
              issuingAuthority: record.issuing_authority,
              street: record.street,
              number: record.number,
              neighborhood: record.neighborhood,
              city: record.city,
              state: record.state
            }
          }
        });
      }

      onSelect();
    } catch (error) {
      console.error('Error loading record data:', error);
      alert('Erro ao carregar dados do registro');
    }
  };

  const getMissingInfo = (record: any): string[] => {
    const missing: string[] = [];
    if (type === 'contract') {
      if (!record.installation_locations || record.installation_locations.length === 0) {
        missing.push('Local de instalacao');
      }
      if (!record.technical_configs || record.technical_configs.length === 0) {
        missing.push('Configuracao tecnica');
      }
      if (!record.financial_terms || record.financial_terms.length === 0) {
        missing.push('Termos financeiros');
      }
    }
    return missing;
  };

  const isIncomplete = (record: any): boolean => {
    return getMissingInfo(record).length > 0;
  };

  const hasPlate = useCallback((recordId: string): boolean => {
    if (!allocationsMap) return false;
    const allocs = allocationsMap.get(recordId);
    return !!allocs && allocs.length > 0;
  }, [allocationsMap]);

  const filteredRecords = useMemo(() => {
    if (!records) return [];

    let result = records;

    if (type === 'contract' && filterStatus !== 'all') {
      result = result.filter(record => {
        const incomplete = isIncomplete(record);
        return filterStatus === 'incomplete' ? incomplete : !incomplete;
      });
    }

    if (type === 'contract' && filterPlate !== 'all') {
      result = result.filter(record => {
        const has = hasPlate(record.id);
        return filterPlate === 'with-plate' ? has : !has;
      });
    }

    return result;
  }, [records, type, filterStatus, filterPlate, hasPlate]);

  const stats = useMemo(() => {
    if (!records || type !== 'contract') return { total: 0, complete: 0, incomplete: 0 };

    const incomplete = records.filter(r => isIncomplete(r)).length;
    return {
      total: records.length,
      complete: records.length - incomplete,
      incomplete
    };
  }, [records, type]);

  const renderPlateCell = (recordId: string) => {
    if (stockLoading) {
      return <span className="text-xs text-gray-400">Carregando...</span>;
    }
    if (stockError) {
      return <span className="text-xs text-gray-400">Indisponível</span>;
    }
    const allocs = allocationsMap?.get(recordId);
    if (!allocs || allocs.length === 0) {
      return <span className="text-xs text-gray-400">Sem placa definida</span>;
    }

    return (
      <div className="flex flex-col gap-0.5">
        {allocs.map((a, i) => (
          <div key={a.id} className="flex items-center gap-1 text-xs">
            <span className="text-gray-700">
              {a.quantidade}x {a.marca} {a.potencia_w}W
            </span>
            {i === 0 && allocs.length > 1 && <span className="text-gray-400">+</span>}
          </div>
        ))}
        <div className="flex gap-1">
          {allocs.map((a) => (
            <span
              key={a.id}
              className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                a.status === 'Separada' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
              }`}
            >
              {a.status}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const exportCSV = () => {
    if (!filteredRecords.length) return;

    let headers: string[];
    let rows: string[][];

    if (type === 'contract') {
      const hasPlateCol = visibleColumns.plate;
      headers = ['Nome', 'CPF', 'Telefone', 'Email', 'Local Instalacao', 'Config Tecnica', 'Termos Financeiros', 'Status', ...(hasPlateCol ? ['Placa'] : []), 'Data'];
      rows = filteredRecords.map((r: any) => {
        const base = [
          r.full_name,
          r.cpf,
          r.phone || '',
          r.email || '',
          r.installation_locations?.length > 0 ? 'Sim' : 'Nao',
          r.technical_configs?.length > 0 ? 'Sim' : 'Nao',
          r.financial_terms?.length > 0 ? 'Sim' : 'Nao',
          isIncomplete(r) ? 'Incompleto' : 'Completo',
        ];
        if (hasPlateCol) {
          base.push(StockAllocationsService.formatAllocationsLabel(allocationsMap?.get(r.id)));
        }
        base.push(format(new Date(r.created_at), 'dd/MM/yyyy'));
        return base;
      });
    } else {
      headers = ['Nome', 'CPF', 'RG', 'Endereco', 'Data'];
      rows = filteredRecords.map((r: any) => [
        r.full_name,
        r.cpf,
        r.rg || '',
        `${r.street}, ${r.number} - ${r.neighborhood}, ${r.city}/${r.state}`,
        format(new Date(r.created_at), 'dd/MM/yyyy')
      ]);
    }

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `historico-${type}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!records || records.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Nenhum registro encontrado.</p>
      </div>
    );
  }

  const columnConfig = type === 'contract' ? CONTRACT_COLUMNS : POA_COLUMNS;

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex flex-wrap gap-3 items-center">
          {type === 'contract' && (
            <>
              <div className="text-sm text-gray-600">
                <span className="font-medium">{stats.total}</span> total |{' '}
                <span className="text-green-600 font-medium">{stats.complete}</span> completos |{' '}
                <span className="text-yellow-600 font-medium">{stats.incomplete}</span> incompletos
              </div>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
                className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todos</option>
                <option value="complete">Completos</option>
                <option value="incomplete">Incompletos</option>
              </select>

              <select
                value={filterPlate}
                onChange={(e) => setFilterPlate(e.target.value as FilterPlate)}
                className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todas as placas</option>
                <option value="with-plate">Com placa</option>
                <option value="without-plate">Sem placa</option>
              </select>

              <button
                onClick={handleRefreshAllocations}
                disabled={stockLoading}
                className="px-3 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
                title="Atualizar alocacoes de placas"
              >
                <RefreshCw className={`w-4 h-4 ${stockLoading ? 'animate-spin' : ''}`} />
                Atualizar
              </button>
            </>
          )}

          <div className="relative">
            <button
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className="px-3 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
            >
              <Columns className="w-4 h-4" />
              Colunas
            </button>
            {showColumnSelector && (
              <div className="absolute left-0 top-full mt-2 bg-white border rounded-lg shadow-lg p-4 z-20 min-w-[200px]">
                <div className="text-xs font-medium text-gray-500 mb-2 uppercase">Selecionar colunas</div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {Object.entries(columnConfig).map(([key, { label }]) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={visibleColumns[key]}
                        onChange={() => setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }))}
                        className="rounded text-blue-600"
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
                <div className="border-t mt-3 pt-3 flex gap-2">
                  <button
                    onClick={() => setVisibleColumns(Object.fromEntries(Object.keys(columnConfig).map(k => [k, true])))}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Mostrar todas
                  </button>
                  <button
                    onClick={() => setVisibleColumns(Object.fromEntries(Object.entries(columnConfig).map(([k, v]) => [k, v.default])))}
                    className="text-xs text-gray-600 hover:underline"
                  >
                    Padrao
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={exportCSV}
            className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm"
          >
            <Download className="w-4 h-4" />
            Exportar
          </button>
        </div>
      </div>

      {/* Aviso de erro do estoque */}
      {type === 'contract' && stockError && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-orange-800 text-sm">
              {stockError.status === 401
                ? 'Senha de acesso incorreta. Confira o STOCK_FEED_SECRET nos dois apps.'
                : stockError.error}
            </p>
            <p className="text-xs text-orange-700 mt-0.5">
              As informacoes de placas alocadas nao estao disponiveis no momento. O restante do app funciona normalmente.
            </p>
          </div>
        </div>
      )}

      {type === 'contract' && stats.incomplete > 0 && filterStatus !== 'complete' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-yellow-800">
              {stats.incomplete} {stats.incomplete === 1 ? 'contrato precisa' : 'contratos precisam'} de dados adicionais
            </p>
            <p className="text-sm text-yellow-700">
              Use o filtro de colunas para identificar quais informacoes estao faltando em cada registro.
            </p>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {type === 'contract' ? (
                <>
                  {visibleColumns.status && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  )}
                  {visibleColumns.name && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                  )}
                  {visibleColumns.cpf && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CPF
                    </th>
                  )}
                  {visibleColumns.phone && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Telefone
                    </th>
                  )}
                  {visibleColumns.email && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                  )}
                  {visibleColumns.location && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Local
                    </th>
                  )}
                  {visibleColumns.technical && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tecnico
                    </th>
                  )}
                  {visibleColumns.financial && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Financeiro
                    </th>
                  )}
                  {visibleColumns.plate && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Placa
                    </th>
                  )}
                  {visibleColumns.created_at && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                  )}
                  {visibleColumns.actions && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acoes
                    </th>
                  )}
                </>
              ) : (
                <>
                  {visibleColumns.name && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                  )}
                  {visibleColumns.cpf && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CPF
                    </th>
                  )}
                  {visibleColumns.rg && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      RG
                    </th>
                  )}
                  {visibleColumns.address && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Endereco
                    </th>
                  )}
                  {visibleColumns.created_at && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                  )}
                  {visibleColumns.actions && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acoes
                    </th>
                  )}
                </>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredRecords.map((record: any) => {
              const missing = getMissingInfo(record);
              const hasLocation = record.installation_locations?.length > 0;
              const hasTechnical = record.technical_configs?.length > 0;
              const hasFinancial = record.financial_terms?.length > 0;

              return (
                <tr key={record.id} className={`hover:bg-gray-50 ${missing.length > 0 ? 'bg-yellow-50' : ''}`}>
                  {type === 'contract' ? (
                    <>
                      {visibleColumns.status && (
                        <td className="px-4 py-4 whitespace-nowrap">
                          {missing.length > 0 ? (
                            <div className="flex items-center group relative">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Incompleto
                              </span>
                              <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-2 px-3 z-10 whitespace-nowrap">
                                <p className="font-medium mb-1">Faltando:</p>
                                {missing.map((item, idx) => (
                                  <p key={idx}>- {item}</p>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Completo
                            </span>
                          )}
                        </td>
                      )}
                      {visibleColumns.name && (
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {record.full_name}
                        </td>
                      )}
                      {visibleColumns.cpf && (
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.cpf}
                        </td>
                      )}
                      {visibleColumns.phone && (
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.phone || <span className="text-gray-400">-</span>}
                        </td>
                      )}
                      {visibleColumns.email && (
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.email || <span className="text-gray-400">-</span>}
                        </td>
                      )}
                      {visibleColumns.location && (
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          {hasLocation ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              OK
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              Falta
                            </span>
                          )}
                        </td>
                      )}
                      {visibleColumns.technical && (
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          {hasTechnical ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              OK
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              Falta
                            </span>
                          )}
                        </td>
                      )}
                      {visibleColumns.financial && (
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          {hasFinancial ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              OK
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              Falta
                            </span>
                          )}
                        </td>
                      )}
                      {visibleColumns.plate && (
                        <td className="px-4 py-4 whitespace-nowrap">
                          {renderPlateCell(record.id)}
                        </td>
                      )}
                      {visibleColumns.created_at && (
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {format(new Date(record.created_at), 'dd/MM/yyyy')}
                        </td>
                      )}
                      {visibleColumns.actions && (
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => setViewingRecord(record)}
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="Visualizar"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleSelectRecord(record)}
                              className="text-green-600 hover:text-green-900 p-1"
                              title="Editar"
                            >
                              <PenSquare className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(record.id)}
                              disabled={isDeleting}
                              className="text-red-600 hover:text-red-900 p-1"
                              title="Excluir"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      )}
                    </>
                  ) : (
                    <>
                      {visibleColumns.name && (
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {record.full_name}
                        </td>
                      )}
                      {visibleColumns.cpf && (
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.cpf}
                        </td>
                      )}
                      {visibleColumns.rg && (
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.rg || <span className="text-gray-400">-</span>}
                        </td>
                      )}
                      {visibleColumns.address && (
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {record.street}, {record.number} - {record.neighborhood}, {record.city}/{record.state}
                        </td>
                      )}
                      {visibleColumns.created_at && (
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {format(new Date(record.created_at), 'dd/MM/yyyy')}
                        </td>
                      )}
                      {visibleColumns.actions && (
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handlePreview(record)}
                              disabled={isGenerating}
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="Visualizar"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleSelectRecord(record)}
                              className="text-green-600 hover:text-green-900 p-1"
                              title="Editar"
                            >
                              <PenSquare className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(record.id)}
                              disabled={isDeleting}
                              className="text-red-600 hover:text-red-900 p-1"
                              title="Excluir"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      )}
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredRecords.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Nenhum registro encontrado com os filtros selecionados.</p>
        </div>
      )}

      {viewingRecord && (
        <ContractViewModal
          record={viewingRecord}
          allocations={allocationsMap?.get(viewingRecord.id)}
          onClose={() => setViewingRecord(null)}
        />
      )}
    </div>
  );
}
